import React, { useState, useEffect, useCallback } from "react";
import { Box, Chip, Tooltip, CircularProgress, IconButton, Typography } from "@mui/material";
import { AutorenewIcon, BadgeIcon, CheckCircleIcon, DownloadIcon, VisibilityIcon, WarningAmberIcon } from "../../../../icons";
import { InputStringMask } from "../../../common/controls";
import { useFormRecord } from "../../../../common/controls/forms/FormRecordContext";
import { restFetch } from "../../../../api/apiClient";
import { downloadWithAuth, fetchFileBlob } from "../../../../common/utils/downloadWithAuth";
import { PDFViewerDialog } from "../../../../common/components/PDFViewerDialog";
import { useRootStore } from "../../../../store/contexts";
import { pedirCargaDeDni } from "../../../../common/pages/dniUpload/pedidoDeCargaDeDni";

/**
 * Campo CUIT/CUIL del autor: reproduce el input enmascarado estándar (máscara
 * condicional `when taxIdType in [cuitCuil]`) y agrega al lado un chip con el
 * estado de la Constancia de CUIL que el bot descarga server-side:
 *   ✅ descargada (link para abrir el PDF)  ⏳ pendiente  ⚠️ no se pudo (motivo)
 *
 * El estado del cert (y el taxIdType para la máscara) se consultan por el
 * recordId que provee FormRecordContext (solo en update; en create aún no hay
 * registro → máscara CUIT por defecto y sin chip hasta el primer guardado).
 * Los campos cuit* son .fillingOnServer: no viajan en las props del form, por
 * eso se leen por query y no por prop.
 *
 * Al final, el ícono «Carga de DNI» (KAN-463), en el alta y en la edición:
 * abre la carga del DNI de este formulario. El diálogo está en FormExtras, que
 * es quien puede volcar lo leído en el formulario (ver pedidoDeCargaDeDni.ts).
 */

interface CertStatus {
  loading: boolean;
  taxIdType?: string;
  nationalityId?: string;
  hasCertificate: boolean;
  filename?: string;
  error?: string;
  errorType?: string;
}

// Espejo de las máscaras condicionales del DSL (taxId): CUIT/CUIL para cuitCuil,
// DNI (7-8 díg.) para du. "Otro ID" no matchea ninguna → input libre sin máscara.
const TAXID_CONDITIONAL_MASKS = [
  {
    mask: "99-9999999-9",
    masks: ["99-9999999-9", "99-99999999-9"],
    when: { field: "taxIdType", operator: "in" as const, values: ["cuitCuil"] },
  },
  {
    mask: "9999999",
    masks: ["9999999", "99999999"],
    when: { field: "taxIdType", operator: "in" as const, values: ["du"] },
  },
];

const TEXT: Record<
  string,
  {
    ready: string;
    view: string;
    download: string;
    failed: string;
    pending: string;
    invalidData: string;
    dniUpload: string;
  }
> = {
  es: {
    ready: "Constancia",
    view: "Ver constancia de CUIL",
    download: "Descargar constancia de CUIL",
    failed: "No se pudo bajar la constancia",
    pending: "Constancia ANSES en proceso",
    invalidData: "Datos personales no válidos",
    dniUpload: "Carga de DNI",
  },
  en: {
    ready: "Certificate",
    view: "View CUIL certificate",
    download: "Download CUIL certificate",
    failed: "Certificate download failed",
    pending: "ANSES certificate in progress",
    invalidData: "Invalid personal data",
    dniUpload: "ID upload",
  },
  pt: {
    ready: "Constância",
    view: "Ver constância de CUIL",
    download: "Baixar constância de CUIL",
    failed: "Não foi possível baixar a constância",
    pending: "Constância ANSES em processo",
    invalidData: "Dados pessoais inválidos",
    dniUpload: "Envio de documento",
  },
};

export interface AuthorComposerTaxIdCustomFieldProps {
  value: any;
  onChange: (value: any) => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  description?: string;
  /** Lo pasa el generador a los campos maybe(...): "(Opcional)" mientras está vacío. */
  optional?: boolean;
}

export const AuthorComposerTaxIdCustomField: React.FC<
  AuthorComposerTaxIdCustomFieldProps
> = ({ value, onChange, error, helperText, label, description, optional }) => {
  const { langStore, activityStore } = useRootStore();
  const { recordId, values } = useFormRecord();
  const t = TEXT[langStore.language] ?? TEXT.es;
  const [status, setStatus] = useState<CertStatus>({
    loading: !!recordId,
    taxIdType: "cuitCuil",
    hasCertificate: false,
  });

  const fetchStatus = useCallback(async () => {
    if (!recordId) return;
    try {
      const data = await restFetch("/graphql", {
        method: "POST",
        body: JSON.stringify({
          query: `query AuthorCuitCertStatus($where: AuthorComposer_Filter!) {
            authorComposerListWithOffset(where: $where, offset: 0, take: 1) {
              taxIdType
              nationality { id }
              cuitCertificate
              cuitCertificateError
              cuitCertificateErrorType
            }
          }`,
          variables: { where: { id: { equals: recordId } } },
        }),
      });
      const record = data?.data?.authorComposerListWithOffset?.[0];
      if (!record) {
        setStatus((s) => ({ ...s, loading: false }));
        return;
      }
      setStatus({
        loading: false,
        taxIdType: record.taxIdType,
        nationalityId: record.nationality?.id,
        hasCertificate: !!record.cuitCertificate,
        filename: record.cuitCertificate || undefined,
        error: record.cuitCertificateError || undefined,
        errorType: record.cuitCertificateErrorType || undefined,
      });
    } catch {
      setStatus((s) => ({ ...s, loading: false }));
    }
  }, [recordId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Ver: modal in-app (PDFViewerDialog con blob URL), igual que los PDF de las
  // grillas. Abrir pestaña nueva (window.open) en modo PWA saca al usuario de
  // la app. El blob URL se revoca al cerrar.
  const [pdfView, setPdfView] = useState<{ url: string; title: string } | null>(null);

  const closePdfView = useCallback(() => {
    setPdfView((prev) => {
      if (prev) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  const renderChip = () => {
    if (!recordId) return null; // create: aún no hay intento de descarga
    if (status.loading) {
      return <CircularProgress size={18} sx={{ ml: 1 }} />;
    }
    // La constancia solo aplica a autores ARGENTINOS: los extranjeros no
    // requieren ANSES y el bot no se dispara para ellos.
    if (status.nationalityId && status.nationalityId !== "Argentina") return null;
    // El bot corre para CUIT/CUIL y también para DU (deriva el DNI); solo
    // "otro ID" queda afuera. Con éxito el server normaliza el tipo a cuitCuil,
    // pero ante error (p.ej. nombre distinto al de ANSES) el tipo queda "du" a
    // propósito — y el motivo DEBE quedar visible igual.
    if (status.taxIdType && !["cuitCuil", "du"].includes(status.taxIdType)) return null;

    if (status.hasCertificate) {
      // Descarga/preview con auth (token), igual que InputFile del sistema: la URL
      // se resuelve como /AuthorComposer.cuitCertificate/<recordId>/<filename>.
      const fileOptions = {
        entityName: "AuthorComposer",
        fieldName: "cuitCertificate",
        instanceId: recordId as string,
        fileName: status.filename || "constancia-cuil.pdf",
      };
      const handleView = () => {
        fetchFileBlob(fileOptions, activityStore)
          .then((blob) => {
            if (!blob) return;
            setPdfView({ url: window.URL.createObjectURL(blob), title: fileOptions.fileName });
          })
          .catch((err: any) => {
            console.error("Error opening file:", err);
          });
      };
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Chip
            size="small"
            color="success"
            icon={<CheckCircleIcon />}
            label={t.ready}
            sx={{ ml: 1 }}
          />
          <Tooltip title={t.view}>
            <IconButton size="small" onClick={handleView}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.download}>
            <IconButton size="small" onClick={() => void downloadWithAuth(fileOptions)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // ANSES rechazó la identidad (pantalla "acredite identidad"): datos inválidos,
    // no se reintenta solo → el usuario debe corregir los datos personales.
    // El motivo se muestra como TEXTO visible (no solo tooltip): el mensaje
    // trae el dato a corregir (p.ej. el nombre exacto según ANSES).
    if (status.errorType === "invalidData") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}>
          <Chip
            size="small"
            color="error"
            icon={<WarningAmberIcon />}
            label={t.invalidData}
            sx={{ ml: 1 }}
          />
          {status.error && (
            <Typography variant="caption" color="error" sx={{ ml: 1, maxWidth: 480 }}>
              {status.error}
            </Typography>
          )}
        </Box>
      );
    }

    // La constancia no se pidió porque al autor le faltan datos (KAN-450): no
    // es una falla de ANSES ni del bot; se dice con la etiqueta del enum. El
    // server guarda solo el código, sin texto de error: esto va antes de «en
    // proceso», que es lo que queda sin constancia y sin error. Si el LangStore
    // no trae la etiqueta (antes de regenerar con el valor nuevo del enum), se
    // usa el texto genérico de la falla.
    if (status.errorType === "incompleteData") {
      const etiqueta =
        langStore.CuitCertificateErrorTypeOptions?.find((o: { id: string }) => o.id === "incompleteData")
          ?.displayId ?? t.failed;
      return (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          icon={<WarningAmberIcon />}
          label={etiqueta}
          sx={{ ml: 1 }}
        />
      );
    }

    if (status.error) {
      return (
        <Tooltip title={status.error}>
          <Chip
            size="small"
            color="warning"
            icon={<WarningAmberIcon />}
            label={t.failed}
            sx={{ ml: 1 }}
          />
        </Tooltip>
      );
    }

    return (
      <Chip
        size="small"
        variant="outlined"
        icon={<AutorenewIcon />}
        label={t.pending}
        sx={{ ml: 1 }}
      />
    );
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
      <InputStringMask
        value={value ?? ""}
        error={error}
        helperText={helperText}
        label={label || ""}
        nullText={optional ? langStore.optionalFieldHint : undefined}
        description={description}
        onChange={(v: string) => onChange(v)}
        conditionalMasks={TAXID_CONDITIONAL_MASKS}
        // El taxIdType VIVO (dropdown) gobierna la máscara — reactivo al tipear —,
        // vía FormRecordContext.values. Fallback al guardado / default solo si el
        // contexto no lo provee (forms viejos sin el provider de values).
        conditionValues={{
          taxIdType:
            (values?.taxIdType as string) ?? status.taxIdType ?? "cuitCuil",
        }}
      />
      {renderChip()}
      <Tooltip title={t.dniUpload}>
        <IconButton size="small" onClick={(evento) => pedirCargaDeDni(evento.currentTarget)}>
          <BadgeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <PDFViewerDialog
        open={pdfView !== null}
        onClose={closePdfView}
        pdfUrl={pdfView?.url ?? ""}
        title={pdfView?.title}
      />
    </Box>
  );
};
